'use client';
import React from 'react';
import Link from 'next/link';
import useTranslation from '../../context/Translation/useTranslation';
import { LinkProps } from '@repo/ui/src/types/link';
import useCurrentUrl from '@repo/ui/src/hooks/useCurrentUrl';
import { prependSlash } from './helpers';

export interface AnchorProps extends Omit<LinkProps, '_meta' | 'isAnchorLink'> {
  children?: React.ReactNode;
  title?: string;
  className?: string;
  shallow?: boolean;
  locale?: string | false;
  disableScroll?: boolean;
  tabIndex?: number;
  dataTestId?: string;
  forcePageLoad?: boolean;
  ariaLabelledBy?: string;
  onClick?: (e: any) => void;
  onMouseEnter?: (event: any) => void;
  onKeyDown?: (event: any) => void;
}

const formatAnchorTag = (tag: string) => {
  if (!tag) return '';
  return tag.startsWith('#') ? tag : `#${tag}`;
};

const extractLocaleFromPath = (path: string) => {
  const match = path.match(/^\/?([a-z]{2})\/([a-z]{2})(\/|$)/);
  return match ? `/${match[1]}/${match[2]}` : '/en/us';
};

export const Anchor = ({
  className,
  children,
  title,
  displayName,
  label,
  isExternal,
  externalLink,
  link,
  noFollow,
  noIndex,
  disableScroll = false,
  tabIndex,
  dataTestId,
  shallow = false,
  locale = false,
  forcePageLoad = false,
  anchorTag = '',
  onClick,
  onMouseEnter,
  onKeyDown,
  ariaLabelledBy,
}: AnchorProps) => {
  const { path, country } = useCurrentUrl();
  const { t } = useTranslation();
  const noFollowText = noFollow ? `nofollow` : ``;
  const noIndexText = noIndex ? `noindex` : ``;
  const _children = children ?? displayName;
  const formattedAnchorTag = formatAnchorTag(anchorTag);

  if (isExternal) {
    return (
      <a
        className={className}
        href={externalLink}
        target="_blank"
        title={t('genericContent.opensInNewTabText', undefined, {
          default: 'Opens in a new tab',
        })}
        rel={`noreferrer ${noIndexText} ${noFollowText}`}
        aria-label={label}
        aria-labelledby={ariaLabelledBy}
        onClick={onClick}
        tabIndex={tabIndex}
        data-testid={dataTestId}
        onMouseEnter={onMouseEnter}
        onKeyDown={onKeyDown}
      >
        {_children}
      </a>
    );
  }

  if (link) {
    const isAnchorLink = link.startsWith('#');
    const _link = `${isAnchorLink ? link : prependSlash(link)}${formattedAnchorTag}`;
    const targetLocale = extractLocaleFromPath(link || '');
    const isLocaleChange = country !== targetLocale;

    return forcePageLoad || isLocaleChange || isAnchorLink ? (
      <a
        href={_link!}
        className={className}
        title={title || displayName}
        onClick={onClick}
        aria-label={label}
        aria-labelledby={ariaLabelledBy}
        tabIndex={tabIndex}
        data-testid={dataTestId}
        aria-current={path === _link ? 'page' : undefined}
        onMouseEnter={onMouseEnter}
        onKeyDown={onKeyDown}
      >
        {_children}
      </a>
    ) : (
      <Link
        href={_link}
        as={_link}
        aria-label={label}
        aria-labelledby={ariaLabelledBy}
        scroll={!disableScroll}
        locale={locale !== false ? locale : undefined}
        prefetch={false}
        shallow={shallow}
        passHref
        className={className}
        title={title || displayName}
        onClick={onClick}
        tabIndex={tabIndex}
        data-testid={dataTestId}
        aria-current={path === _link ? 'page' : undefined}
        onMouseEnter={onMouseEnter}
        onKeyDown={onKeyDown}
      >
        {_children}
      </Link>
    );
  }

  return _children ? (
    <div className={className} onClick={onClick} data-testid={dataTestId}>
      {_children}
    </div>
  ) : null;
};
